import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useAppTheme } from '../../contexts/ThemeContext';

const BillingSettings = ({ handleSaveSettings }) => {
  const { palette, Colors } = useAppTheme();

  const [billingData] = useState({
    currentPlan: {
      name: 'Professional Plan',
      price: 'month',
      billingCycle: 'billed annually',
      description: 'Includes up to 50 properties, unlimited tenants, and premium support.'
    },
    paymentMethod: {
      type: 'Visa',
      lastFour: '1234',
      expiryDate: '12/26'
    },
    billingHistory: [
      { id: 1, date: 'Mar 01, 2025', description: 'Subscription - Professional Plan', amount: '00', status: 'Paid' },
      { id: 2, date: 'Feb 01, 2025', description: 'Subscription - Professional Plan', amount: '00', status: 'Paid' },
    ]
  });

  const handleChangePlan = () => console.log('Change plan clicked');
  const handleUpdatePayment = () => console.log('Update payment method clicked');
  const handleDownloadInvoice = (id) => console.log(`Downloading invoice ${id}`);

  const cardSx = {
    mb: 3,
    backgroundColor: palette.background.tertiary,
    border: `1px solid ${palette.border}`,
    boxShadow: 'none',
  };

  const outlinedBtnSx = {
    borderColor: Colors.primary.main,
    color: Colors.primary.main,
    '&:hover': { borderColor: Colors.primary.dark, backgroundColor: `${Colors.primary.main}10` },
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: palette.text.primary }}>
        Billing Settings
      </Typography>

      {/* Current Plan */}
      <Card sx={cardSx}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: palette.text.primary }}>
            Current Plan
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: palette.text.primary }}>
                  {billingData.currentPlan.name}
                </Typography>
                <Chip label="Active" size="small" sx={{ bgcolor: `${Colors.primary.main}20`, color: Colors.primary.main, fontWeight: 600 }} />
              </Box>
              <Typography variant="body2" sx={{ color: palette.text.secondary }}>
                {billingData.currentPlan.price}, {billingData.currentPlan.billingCycle}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: palette.text.tertiary }}>
                {billingData.currentPlan.description}
              </Typography>
            </Box>
            <Button variant="outlined" onClick={handleChangePlan} sx={outlinedBtnSx}>
              Change Plan
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Payment Method */}
      <Card sx={cardSx}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: palette.text.primary }}>
            Payment Method
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1.5, borderRadius: 1,
              backgroundColor: palette.background.secondary,
              border: `1px solid ${palette.border}`,
              display: 'flex', alignItems: 'center'
            }}>
              <CreditCardIcon sx={{ color: Colors.primary.main }} />
            </Box>
            <Box>
              <Typography variant="body1" sx={{ color: palette.text.primary }}>
                {billingData.paymentMethod.type} ending in {billingData.paymentMethod.lastFour}
              </Typography>
              <Typography variant="body2" sx={{ color: palette.text.secondary }}>
                Expires {billingData.paymentMethod.expiryDate}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" size="small" onClick={handleUpdatePayment} sx={outlinedBtnSx}>
              Update Payment Method
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card sx={cardSx}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: palette.text.primary }}>
            Billing History
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                {['Date', 'Description', 'Amount', 'Status', 'Invoice'].map((h) => (
                  <TableCell key={h} sx={{ color: palette.text.secondary, borderColor: palette.border, fontWeight: 600 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {billingData.billingHistory.map((invoice) => (
                <TableRow key={invoice.id} sx={{ '&:hover': { backgroundColor: palette.background.primary } }}>
                  <TableCell sx={{ color: palette.text.primary, borderColor: palette.border }}>{invoice.date}</TableCell>
                  <TableCell sx={{ color: palette.text.primary, borderColor: palette.border }}>{invoice.description}</TableCell>
                  <TableCell sx={{ color: palette.text.primary, borderColor: palette.border, fontWeight: 600 }}>{invoice.amount}</TableCell>
                  <TableCell sx={{ borderColor: palette.border }}>
                    <Chip label={invoice.status} size="small" sx={{ bgcolor: `${Colors.status.success}20`, color: Colors.status.success, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell sx={{ borderColor: palette.border }}>
                    <Button
                      size="small"
                      startIcon={<CloudDownloadIcon />}
                      onClick={() => handleDownloadInvoice(invoice.id)}
                      sx={{ color: Colors.primary.main }}
                    >
                      PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BillingSettings;